import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import OutsideClickHandler from "react-outside-click-handler";

import { faChevronDown, faChevronUp } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faCaretDown, faCaretUp } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import EventEmitter from "events";
import { noop } from "lodash";
import { Popup } from "semantic-ui-react";

import ViewportContext from "~/common/ViewportContext";
import { ScrollContext } from "~/layout/ScrollContext";
import { setTooltip } from "~/lib/utils";
import Button, { type ButtonProps } from "~/ui/button";
import { Modal } from "~/ui/legacyFellowIndex";

import styles from "./ArrowDropdown.module.css";

/** We use this to automatically close one dropdown when another is opened */
const openEmitter = new EventEmitter();
openEmitter.setMaxListeners(50); // the default is 10, which we go over frequently

type DropdownItemProps = Omit<React.HTMLProps<HTMLButtonElement>, "type"> & {
	icon?: React.ReactNode;
	selected?: boolean;
	destructive?: boolean;
	disabledHelpText?: string;
};

export function DropdownItem({
	icon,
	children,
	className,
	selected,
	destructive,
	disabledHelpText,
	onClick,
	...buttonProps
}: React.PropsWithChildren<DropdownItemProps>) {
	const closeFn = React.useContext(CloseFnContext);
	const tooltipProps = buttonProps.disabled && disabledHelpText ? setTooltip(disabledHelpText, "bottom center") : {};

	const handleAndClose = React.useCallback<NonNullable<typeof onClick>>(
		event => {
			onClick?.(event);
			closeFn();
		},
		[onClick, closeFn],
	);

	return (
		<button
			type="button"
			className={cx(styles.item, { [styles.selected]: selected, [styles.destructive]: destructive }, className)}
			onClick={handleAndClose}
			{...buttonProps}
			{...tooltipProps}
		>
			{icon}
			<div className={styles.itemText}>{children}</div>
		</button>
	);
}

DropdownItem.Icon = function DropdownItemIcon({
	className,
	icon,
	...rest
}: Omit<React.ComponentProps<typeof FontAwesomeIcon>, "icon"> & { icon: IconDefinition }) {
	return (
		<div className={styles.itemIcon}>
			<FontAwesomeIcon className={cx(styles.dropdownFAIcon, className)} icon={icon} {...rest} />
		</div>
	);
};

type DropdownProps = Omit<ButtonProps, "slim" | "variant"> & {
	text?: React.ReactNode;
	trigger?: React.ReactElement<{ onClick: (e: React.MouseEvent) => void }>;
	icon?: IconDefinition;
	basic?: boolean;
	compact?: boolean;
	light?: boolean;
	heading: string;
	mobileFooter?: React.ReactNode;
	hideOnScroll?: boolean;
	onOpenChange?: (open: boolean) => void;
	popupClassName?: string;
	textClassName?: string;
	useChevronIcon?: boolean;
	hideArrow?: boolean;
	// This prop should be used to add an OutsideClickHandler to the popup that will
	// Close the dropdown when clicking outside of it. This is used to solve the issue
	// With this dropdown being used inside a Dialog. Both Reach's Dialog and Semantic's Popup uses Portals
	// and, as of today, Popup does not support being rendered inside a specific element.
	// Reach's Dialog stops the propagation on clicks inside the modal content. So we need
	// either to pass a onClick listened to the Dialog and handle it here, or take the easiest
	// route which is to use the outside-click-handler package to handle this.
	// There's probably a better way to solve this but this is sufficient for now.
	forceCloseOnOutsideClick?: boolean;
};

const CloseFnContext = React.createContext<() => void>(noop);

/**
 * A dropdown that can be used to select a value.
 * @deprecated Use the new {@link Selector} or {@link MultiSelector} base class instead.
 */
const ArrowDropdown = React.forwardRef<HTMLButtonElement, React.PropsWithChildren<DropdownProps>>(
	(
		{
			mobileFooter,
			heading,
			basic = true,
			compact = false,
			trigger,
			text,
			icon,
			light = false,
			hideOnScroll = !window.Cypress,
			onOpenChange,
			children,
			className,
			popupClassName,
			textClassName,
			useChevronIcon = false,
			hideArrow,
			forceCloseOnOutsideClick,
			...rest
		},
		ref,
	) => {
		const { isMobile } = useContext(ViewportContext);
		const scrollContainer = useContext(ScrollContext);
		const [active, setActive] = useState(false);
		const elementId = useRef(Math.random().toString(36).substr(2));
		const { upArrowIcon, downArrowIcon } = useChevronIcon
			? { upArrowIcon: faChevronUp, downArrowIcon: faChevronDown }
			: { upArrowIcon: faCaretUp, downArrowIcon: faCaretDown };
		const innerButtonRef = useRef<HTMLButtonElement>(null);

		const open = useCallback(
			(e: React.MouseEvent) => {
				if (onOpenChange) onOpenChange(true);
				setActive(true);
				e.stopPropagation();
				openEmitter.emit("ArrowDropdown.open", elementId.current);
			},
			[onOpenChange],
		);

		const close = useCallback(
			function close() {
				if (onOpenChange) onOpenChange(false);
				setActive(false);
			},
			[onOpenChange],
		);

		const toggle = useCallback(
			() =>
				setActive(a => {
					if (onOpenChange) onOpenChange(!a);
					return !a;
				}),
			[onOpenChange],
		);

		const handleOpenMessage = useCallback(
			(emitterElementId: string) => {
				if (emitterElementId !== elementId.current) {
					close();
				}
			},
			[close],
		);

		useEffect(() => {
			openEmitter.on("ArrowDropdown.open", handleOpenMessage);
			return () => {
				openEmitter.removeListener("ArrowDropdown.open", handleOpenMessage);
			};
		}, [handleOpenMessage]);

		useEffect(() => {
			if (scrollContainer && hideOnScroll) {
				scrollContainer.addEventListener("scroll", close);
				return () => scrollContainer.removeEventListener("scroll", close);
			}
			return noop;
		}, [close, hideOnScroll, scrollContainer]);

		let triggerElement;
		if (React.isValidElement(trigger)) {
			const { onClick } = trigger.props;
			triggerElement = React.cloneElement(trigger, {
				onClick: (e: React.MouseEvent) => {
					if (onClick) {
						onClick(e);
					}
					open(e);
				},
			});
		} else {
			triggerElement = (
				<Button
					type="button"
					size="wide"
					variant="tertiary"
					onClick={toggle}
					className={cx(styles.button, className, {
						[styles.active]: active,
						[styles.light]: light,
						[styles.compact]: compact,
					})}
					{...rest}
					ref={ref || innerButtonRef}
				>
					<div className={styles.textContainer}>
						{icon && <FontAwesomeIcon icon={icon} className={styles.dropdownIcon} />}
						<div className={cx(styles.text, textClassName)}>{text}</div>
					</div>
					{!hideArrow && (
						<div
							className={cx(styles.iconContainer, {
								[styles.iconContainerChevron]: useChevronIcon,
							})}
						>
							<FontAwesomeIcon icon={active ? upArrowIcon : downArrowIcon} />
						</div>
					)}
				</Button>
			);
		}

		const contents = <CloseFnContext.Provider value={close}>{children}</CloseFnContext.Provider>;

		const popupContent = (
			<div className={styles.barrier} data-test-id="arrowdropdown-popup">
				{contents}
			</div>
		);

		const onOutsideClick = React.useCallback(
			(event: MouseEvent) => {
				const targetNode = event.target as Node;
				if (ref && typeof ref !== "function" && ref.current?.contains(targetNode)) {
					return;
				}
				if (innerButtonRef.current?.contains(targetNode)) {
					return;
				}
				close();
			},
			[close, ref, innerButtonRef],
		);

		if (isMobile) {
			return children ? (
				<>
					{triggerElement}
					<Modal isOpen={active} onClose={close} className={styles.modal}>
						<Modal.Header onClose={close}>
							<Modal.Title>{heading}</Modal.Title>
						</Modal.Header>
						<Modal.Body className={styles.modalBody}>{contents}</Modal.Body>
						{mobileFooter && <Modal.Footer>{mobileFooter}</Modal.Footer>}
					</Modal>
				</>
			) : null;
		}

		return (
			<Popup
				basic={basic}
				trigger={triggerElement}
				open={active}
				className={cx(styles.popup, popupClassName)}
				on="click"
				position="bottom right"
				onOpen={open}
				onClose={close}
			>
				<>
					{forceCloseOnOutsideClick ? (
						<OutsideClickHandler onOutsideClick={onOutsideClick}>{popupContent}</OutsideClickHandler>
					) : (
						popupContent
					)}
				</>
			</Popup>
		);
	},
);

ArrowDropdown.displayName = "ArrowDropdown";

export default ArrowDropdown;
